import { useCallback, useEffect, useState } from "react";
import { Button, Icon, useToast } from "@astrum/ui";
import { enablePush, pushState } from "../lib/push.js";
import "./push-prompt.css";

/** Remembers a decline, so this is asked once rather than every session. */
const DISMISSED_KEY = "astrum.push.prompt.dismissed";

/**
 * Asks, once, whether to turn on desktop notifications.
 *
 * The control has always existed on the profile page, which is the wrong place
 * to *discover* it: nobody visits their profile to find out a feature exists,
 * so in practice the deployment had push configured and nobody subscribed.
 *
 * Deliberately not a modal. A browser permission prompt appears the moment you
 * agree, and stacking our dialog under the browser's own is confusing — this
 * is a card in the corner that can be ignored.
 *
 * Shown only when it can actually do something: push configured on the server,
 * supported by this browser, not already on, not already refused, and not
 * previously dismissed. In every other case it renders nothing rather than
 * offering a button that would fail.
 */
export function PushPrompt() {
  const { toast } = useToast();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Delayed rather than immediate: arriving at a new app and being asked for
    // a permission before the page has even settled is how people learn to
    // dismiss prompts without reading them.
    const timer = setTimeout(() => {
      if (localStorage.getItem(DISMISSED_KEY) === "true") return;

      void pushState().then((state) => {
        // "off" is the only actionable state. unsupported/unconfigured/denied
        // cannot be fixed by clicking a button here, and "on" is done.
        if (!cancelled && state === "off") setVisible(true);
      });
    }, 4000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISSED_KEY, "true");
    } catch {
      // Private browsing, or storage full. It will ask again next session,
      // which is a smaller problem than failing to render.
    }
  }, []);

  const turnOn = useCallback(() => {
    setBusy(true);
    void enablePush()
      .then((state) => {
        setBusy(false);
        setVisible(false);
        if (state === "on") {
          toast("Desktop notifications are on for this browser.");
          return;
        }
        if (state === "denied") {
          // The browser will not ask twice, so saying "try again" would be a lie.
          toast("Your browser blocked notifications. You can allow them in its site settings.", {
            tone: "danger",
          });
        }
        // Any other outcome means it quietly did not happen; the profile page
        // still has the control, so there is nothing more to say here.
        try {
          localStorage.setItem(DISMISSED_KEY, "true");
        } catch {
          // As above.
        }
      })
      .catch(() => {
        setBusy(false);
        toast("Could not turn notifications on.", { tone: "danger" });
      });
  }, [toast]);

  if (!visible) return null;

  return (
    <div className="push-prompt" role="status">
      <span className="push-prompt__icon">
        <Icon name="bell" size={18} />
      </span>

      <div className="push-prompt__body">
        <strong>Get notified when Astrum Connect is closed</strong>
        <p>
          Messages and mentions arrive on your desktop, even with the tab shut. Per browser — turn it on
          again on your other machines.
        </p>
      </div>

      <div className="push-prompt__actions">
        <Button size="sm" onClick={turnOn} disabled={busy}>
          {busy ? "Turning on…" : "Turn on"}
        </Button>
        <Button size="sm" variant="ghost" onClick={dismiss} disabled={busy}>
          Not now
        </Button>
      </div>

      <button type="button" className="push-prompt__close" aria-label="Dismiss" onClick={dismiss}>
        <Icon name="close" size={13} />
      </button>
    </div>
  );
}
